// Plunk Autopilot: client bits shared by the Autopilot page and the approve-a-draft card.
// API: /v1/campaign/autopilot/* on CampaignService (spec PLUNK_AUTOPILOT_SPEC_2026-09-26.md §11).
// Everything here fails soft: a 404 (service not deployed yet) reads as "nothing yet".
import ServiceFactory from "../../service";
import account from "../../data/account";
import endpoints from "../../endpoints";
import { AuthBase } from "../../../plunk/service/auth-base";
export function autopilotSvc(api = "") {
  const Service = ServiceFactory.getType(`/campaign/ad/autopilot${api}`, "product"); // /v1 root — under the ad route the gateway sends to CampaignService
  return new Service();
}

// Approvals go through the ApprovalController, which wants the ACCESS token (the ID token
// 401s) — raw fetch, same as views/campaign-review.
export async function approvalCall(path, method = "GET", body) {
  const base = endpoints.activity && endpoints.activity.root ? endpoints.activity.root.replace(/\/v1\/activity$/, "") : "";
  const res = await fetch(`${base}/v1/campaign${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + (AuthBase.accessToken || ""),
      "x-app-id": (account.get() || {}).tenantId || ""
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  const text = await res.text();
  let data = {};
  try {
    data = JSON.parse(text);
  } catch (e) {/* non-JSON */}
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}
export const toast = msg => {
  try {
    const t = document.querySelector("paper-toast");
    if (t && msg) t.show(msg);
  } catch (e) {/* no toast host */}
};
export const RULE_LABELS = {
  market_diagnostics: "Market diagnostics",
  bulk_image_edit: "Bulk image edit",
  content_commerce: "Product copy gap",
  content_refresh: "Refresh",
  content_coverage: "Coverage gap",
  content_content_gap: "AI visibility gap",
  content_winner_amp: "Amplify a winner",
  inventory_push: "Inventory-driven push",
  stock_protection: "Stock protection",
  budget_shift: "Budget shift",
  wholesale_track: "Wholesale track",
  segment_reaction: "Segment reaction",
  market_gap: "Market gap",
  fatigue: "Creative fatigue",
  stop_rule: "Stop rule"
};
export const ruleLabel = r => RULE_LABELS[r] || String(r || "").replace(/[_-]+/g, " ").replace(/^\w/, c => c.toUpperCase()) || "Recommendation";
export const EVENT_KINDS = {
  // [label, tone] — tone maps to .ap-chip.{on|warn|info|bad}; empty = neutral
  analyzed: ["Analyzed", ""],
  recommended: ["Recommended", ""],
  drafted: ["Drafted", "warn"],
  approved: ["Approved", "on"],
  launched: ["Launched", "on"],
  adjusted: ["Adjusted", "info"],
  paused: ["Paused", "warn"],
  resumed: ["Resumed", "on"],
  stopped: ["Stopped", "bad"],
  imported: ["Imported", ""],
  reverted: ["Reverted", ""],
  rejected: ["Rejected", "bad"],
  dismissed: ["Dismissed", ""],
  config: ["Config", ""]
};

// Channel catalog for the configuration panel. `needs` = a connection the tenant must have;
// the page marks a channel "Not connected" when it's absent (ownAdAccounts / provider settings).
export const CHANNELS = [{
  key: "content",
  short: "Content",
  label: "Content pieces",
  sub: "drafts, refreshes, coverage gaps from the Content Hub queue",
  defaultOn: true
}, {
  key: "markets",
  short: "Market reports",
  label: "Market diagnostics",
  sub: "one scorecard per store market, every test against the rest of the chain",
  defaultOn: true
}, {
  key: "google_search",
  short: "Google Search",
  label: "Google Search + Performance Max",
  needs: "google",
  defaultOn: true
}, {
  key: "youtube",
  short: "YouTube",
  label: "YouTube · Demand Gen",
  needs: "google",
  defaultOn: false
}, {
  key: "ctv",
  short: "Streaming TV",
  label: "CTV / streaming",
  needs: "google",
  defaultOn: false
}, {
  key: "meta",
  short: "Meta",
  label: "Meta Ads",
  needs: "facebook",
  defaultOn: false
}, {
  key: "email",
  short: "Email",
  label: "Email journeys",
  defaultOn: true
}, {
  key: "microsoft",
  short: "Microsoft",
  label: "Microsoft Advertising",
  needs: "microsoft",
  defaultOn: false
}];
export const DEFAULT_GUARDRAILS = {
  markets: [],
  language: "English",
  dailyCapUsd: 300,
  monthlyCapUsd: 9000,
  audienceRule: "retail_paid_wholesale_email",
  // retail: paid + email · wholesale: email only
  blackoutDates: [],
  inventoryFloorDays: 14,
  marginFloorPct: 0,
  maxAdjustmentsPerCampaignPerDay: 1,
  maxNewDraftsPerWeek: 3
};
export const DEFAULT_PERMISSIONS = {
  create: "ask",
  update: "allow",
  pause: "allow",
  raiseCap: "never"
};
export const DEFAULT_CHANNELS = CHANNELS.reduce((o, c) => {
  o[c.key] = !!c.defaultOn;
  return o;
}, {});
export const POSITIONING = "Plunk works alongside Shopify Autopilot. Autopilot runs your Meta and Shop campaigns; Plunk adds Google Search, Performance Max, YouTube and CTV, email journeys, demand forecasting, Studio creative, and attribution across your store, POS, ERP and wholesale sales, all in one place.";
export const money = v => {
  const n = Number(v);
  return isFinite(n) ? n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }) : "-";
};
export const money2 = v => {
  const n = Number(v);
  return isFinite(n) ? n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }) : "-";
};
export const when = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? String(iso) : d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
};
export const dayOf = iso => {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (isNaN(d)) return "Earlier";
  const t = new Date();
  const y = new Date(t);
  y.setDate(t.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  return same(d, t) ? "Today" : same(d, y) ? "Yesterday" : d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
};

// Titles written before 2026-09-29 by the content queue carry an em dash; the App copy rule says colon.
export const cleanTitle = t => String(t || "").replace(/\s+—\s+/g, ": ");

// ---------------------------------------------------------------- redesign helpers (2026-10-09)
// Plain-language copy for the Autopilot page and the Rules page. Nothing here renders money on its own:
// every money string is built only when the caller passes moneyVisible (managed workspaces never see it).

export const AUDIENCE_RULES = [["retail_paid_wholesale_email", "Shoppers get ads and email · business buyers get email only"], ["retail_only", "Shoppers only · no outreach to business buyers"], ["all_paid", "Everyone can get ads and email"]];
export const PERMISSION_OPTIONS = [["ask", "Ask me first"], ["allow", "Do it and tell me"], ["never", "Never"]];

/** Normalized config from GET status (defaults filled in). */
export function cfgFromStatus(status) {
  const c = status && status.config || {};
  return {
    enabled: !!(status && status.enabled),
    channels: {
      ...DEFAULT_CHANNELS,
      ...(c.channels || {})
    },
    guardrails: {
      ...DEFAULT_GUARDRAILS,
      ...(c.guardrails || {})
    },
    permissions: {
      ...DEFAULT_PERMISSIONS,
      ...(c.permissions || {})
    },
    aiDailyCapUsd: c.aiDailyCapUsd != null ? c.aiDailyCapUsd : 0.5
  };
}
export const configBody = cfg => ({
  enabled: cfg.enabled,
  channels: cfg.channels,
  guardrails: cfg.guardrails,
  permissions: cfg.permissions,
  aiDailyCapUsd: cfg.aiDailyCapUsd
});

/** Workspace owners and administrators may change the rules (PUT /config is Administrator-gated server side). */
export function isAutopilotAdmin() {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root"].some(g => groups.includes(g));
}
export const parsePayload = r => {
  const p = r && r.payload;
  if (p && typeof p === "string") {
    try {
      return JSON.parse(p);
    } catch (e) {
      return {};
    }
  }
  return p || {};
};

/** "tonight at 12:45 AM" / "today at 3:00 PM" / "tomorrow at 9:00 AM" / "Oct 12 at 4:45 AM". */
export function nextLookText(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const now = new Date();
  const t = when(iso);
  const h = d.getHours();
  const diffH = (d - now) / 36e5;
  if (diffH < 0) return `soon`;
  if (diffH <= 12 && (h < 6 || h >= 18)) return `tonight at ${t}`;
  if (d.toDateString() === now.toDateString()) return `today at ${t}`;
  const tm = new Date(now);
  tm.setDate(now.getDate() + 1);
  if (d.toDateString() === tm.toDateString()) return `tomorrow at ${t}`;
  return `${d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  })} at ${t}`;
}

// Lower-case only a leading verb ("You approved promote X" never lower-cases a product or place name).
const lcFirst = s => s && /^(Promote|Pause|Rotate|Shift|Remove|Add|Run|Write|Draft|Send|Stop|Reply|Raise|Move|Refresh|Cover|Answer|Amplify|Reorder|Win|Abandoned|New|Local|Review|Sale)\b/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s;
const n0 = v => Number(String(v).replace(/,/g, ""));
const num = v => isFinite(Number(v)) ? Number(v).toLocaleString() : String(v);
const firstSentence = s => {
  const t = String(s || "").trim();
  const m = t.match(/^.*?[.!?](?=\s|$)/);
  return (m ? m[0] : t).trim();
};

/** Removes every dollar figure (defense in depth for managed workspaces). */
export const scrubMoney = s => String(s || "").replace(/\s*\(?[-+]?\$[\d,.]+(?:\s?[kKmM])?\)?/g, "").replace(/\s{2,}/g, " ").replace(/\s+([,.;:])/g, "$1").trim();

/** Plain copy for any stored title or sentence: no em dashes, no jargon, no internal prefixes. */
export const plainText = t => cleanTitle(t).replace(/\s+[—–]\s+/g, ": ").replace(/—/g, ", ").replace(/\s*\((?:ROAS|CTR)[^)]*\)/gi, "").replace(/\bROAS\b/g, "return on ad spend").replace(/\bCTR\b/g, "clicks").replace(/the rest of the chain/gi, "your other locations").replace(/\bchain\b/gi, "other locations").trim();
const CONTENT_PREFIX = /^(Refresh|Amplify|Product copy|Answer the gap|Cover):\s*/i;
export const plainTitle = t => plainText(String(t || "").replace(CONTENT_PREFIX, "")).replace(/^Autopilot\s+/, "");
const unquote = x => plainText(String(x || "").replace(/"{2,}/g, '"').replace(/^"+|"+$/g, ""));
const fmtUsd = v => `$${n0(v).toLocaleString(undefined, {
  maximumFractionDigits: 0
})}`;

/** Content Hub queue titles → { title, why, name }. Same wording as the Content Hub Recommendations tab. */
export function contentCopy(rawTitle, detail, moneyVisible) {
  const raw = String(rawTitle || "").replace(CONTENT_PREFIX, "");
  let m = raw.match(/^"?(.+?)"? is fading: (\w+) fell ([\d.]+)%/i);
  if (m) {
    const unit = /engage/i.test(m[2]) ? "interactions" : "visits";
    const pct = Number(m[3]);
    const name = unquote(m[1]);
    return {
      name,
      title: pct >= 99.95 ? `${name} had no ${unit} this month` : `${name} is getting fewer ${unit}`,
      why: pct >= 99.95 ? `No ${unit} this month` : `${unit.charAt(0).toUpperCase() + unit.slice(1)} down ${Math.round(pct)}% on the month before`
    };
  }
  m = raw.match(/^(.*): top seller [—-] no linked content \(([\d,.]+) revenue, (\d+)d\)/i);
  if (m) return {
    name: unquote(m[1]),
    title: `Nothing written about ${unquote(m[1])}`,
    why: moneyVisible ? `One of your top sellers: ${fmtUsd(m[2])} in the last ${m[3]} days` : `One of your top sellers over the last ${m[3]} days`
  };
  m = raw.match(/^(.*): high traffic [—-] no linked content \(([\d,.]+) views, last quarter\)/i);
  if (m) return {
    name: unquote(m[1]),
    title: `Nothing written about ${unquote(m[1])}`,
    why: `Popular with visitors: ${num(n0(m[2]))} views last quarter`
  };
  m = raw.match(/^(.*): AI answers mention competitors (\d+)x on this topic/i);
  if (m) return {
    name: unquote(m[1]),
    title: `Nothing written about ${unquote(m[1])}`,
    why: `AI assistants named other brands ${num(n0(m[2]))} times on this in the last 30 days`
  };
  m = raw.match(/AI engines rarely mention you on "(.+?)" \((\d+)% visibility\)/i);
  if (m) return {
    name: unquote(m[1]),
    title: `AI assistants rarely mention you for ${unquote(m[1])}`,
    why: `You came up in ${m[2]}% of the answers checked`
  };
  m = raw.match(/^"?(.+?)"? had ([\d,]+) views this month but only (\d+) characters of description/i);
  if (m) return {
    name: unquote(m[1]),
    views: n0(m[2]),
    chars: Number(m[3]),
    title: `${unquote(m[1])} needs a fuller description`,
    why: `${num(n0(m[2]))} views this month, ${Number(m[3]) === 0 ? "and no description yet" : `but only ${m[3]} characters of description`}`
  };
  m = raw.match(/^"?(.+?)"? earned (.+?) \(28d\) from a single channel/i);
  if (m) {
    let why = "Doing well, all from one channel";
    let k;
    if (k = m[2].match(/^([\d,.]+) in attributed sales/i)) why = moneyVisible ? `${fmtUsd(k[1])} in sales in the last 4 weeks, all from one channel` : "Selling well in the last 4 weeks, all from one channel";else if (k = m[2].match(/^([\d,]+) sessions/i)) why = `${num(n0(k[1]))} visits in the last 4 weeks, all from one channel`;else if (k = m[2].match(/^([\d,]+) engagements/i)) why = `${num(n0(k[1]))} interactions in the last 4 weeks, all from one channel`;
    return {
      name: unquote(m[1]),
      title: `${unquote(m[1])} is doing well on one channel`,
      why
    };
  }
  const title = plainTitle(raw);
  const why = detail ? firstSentence(plainText(detail)) : "";
  return {
    name: title,
    title,
    why: moneyVisible ? why : scrubMoney(why)
  };
}

/** High / Medium / Low. Content and market ideas score 0-100; campaign ideas score sales-per-dollar. */
export function impactOf(r) {
  const s = Number(r && r.score) || 0;
  const hundred = /^(content_|market_)/.test(String(r && r.rule || "")) || r.kind === "content" || r.kind === "market" || s > 10;
  // Changes to running campaigns (adjustments) score on their own small scale: protective ones are Medium, the rest Low.
  const lvl = r.kind === "adjustment" ? /^(stop_rule|stock_protection)$/.test(String(r.rule)) ? 2 : 1 : hundred ? s >= 60 ? 3 : s >= 40 ? 2 : 1 : s >= 2 ? 3 : s >= 1 ? 2 : 1;
  return [lvl, lvl === 3 ? "high" : lvl === 2 ? "medium" : "low", lvl === 3 ? "High" : lvl === 2 ? "Medium" : "Low"];
}
const PAID_PLAIN = {
  search: "Google",
  display: "Google",
  pmax: "Google",
  "performance max": "Google",
  youtube: "YouTube",
  ctv: "streaming TV",
  social: "Meta",
  meta: "Meta",
  email: "email"
};
export function channelsPlain(list) {
  const out = [];
  (list || []).forEach(c => {
    const k = String(typeof c === "string" ? c : c && (c.name || c.channel) || "").toLowerCase();
    const v = PAID_PLAIN[k] || k;
    if (v && !out.includes(v)) out.push(v);
  });
  return out.length <= 1 ? out.join("") : `${out.slice(0, -1).join(", ")} and ${out[out.length - 1]}`;
}
export const namesList = (names, shown = 2) => {
  const n = names.filter(Boolean).filter((x, i, a) => a.indexOf(x) === i);
  if (!n.length) return "";
  if (n.length <= shown + 1) return n.length === 1 ? n[0] : `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
  return `${n.slice(0, shown).join(", ")} and ${n.length - shown} more`;
};
const daysOf = p => {
  if (p.days) return Number(p.days);
  if (p.startDate && p.endDate) {
    const d = Math.round((new Date(p.endDate) - new Date(p.startDate)) / 864e5);
    return d > 0 ? d : null;
  }
  return null;
};

/** One plain reason line for a single idea (real numbers from the payload; money only when visible). */
export function reasonFor(r, moneyVisible) {
  const p = parsePayload(r);
  const prod = p.product || null;
  const parts = [];
  if (r.rule === "inventory_push" && prod) {
    const rev = Number(prod.Revenue30 || prod.revenue30);
    parts.push(moneyVisible && rev > 0 ? `${money(rev)} in sales in the last 30 days` : "Sells steadily");
    const doc = prod.DaysOfCover != null ? Number(prod.DaysOfCover) : null;
    parts[0] += doc != null ? ` and ${Math.round(doc).toLocaleString()} days of stock` : " and in stock";
  } else {
    const base = r.rationale || p.detail || "";
    if (base) parts.push(firstSentence(plainText(base)));
  }
  const days = daysOf(p);
  if (moneyVisible && Number(r.expectedLiftUsd) > 0) parts.push(`about ${money(r.expectedLiftUsd)} in sales${days ? ` over ${days} days` : ""}`);
  const s = parts.filter(Boolean).join(" · ");
  return moneyVisible ? s : scrubMoney(s);
}
const GROUP_TITLES = {
  content_commerce: n => `${n} product pages need a fuller description`,
  content_refresh: n => `${n} pages are losing visits`,
  content_coverage: n => `${n} things people look for have nothing written about them`,
  content_content_gap: n => `${n} topics where AI assistants name other brands`,
  content_winner_amp: n => `${n} pieces are doing well on one channel`
};

/** Open recommendations → idea rows. Same-rule content items fold into one row ("A, B and N more"). Sorted most valuable first. */
export function ideaRows(open, moneyVisible) {
  const groups = new Map();
  open.forEach(r => {
    const key = r.kind === "content" && /^content_/.test(String(r.rule || "")) ? `rule:${r.rule}` : `id:${r.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });
  const rows = [];
  groups.forEach((recs, key) => {
    recs.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
    const r0 = recs[0];
    const p0 = parsePayload(r0);
    const imp = recs.map(impactOf).sort((a, b) => b[0] - a[0])[0];
    const lift = recs.reduce((t, r) => t + (Number(r.expectedLiftUsd) || 0), 0);
    const inHub = r0.kind === "content" && !!p0.contentRecommendationId && r0.rule !== "content_commerce";
    let title, why;
    if (r0.kind === "content" && /^content_/.test(String(r0.rule || ""))) {
      const copies = recs.map(r => contentCopy(r.title, parsePayload(r).detail, moneyVisible));
      if (recs.length === 1) {
        title = copies[0].title;
        why = copies[0].why;
      } else {
        title = (GROUP_TITLES[r0.rule] || (n => `${n} ${ruleLabel(r0.rule).toLowerCase()} ideas`))(recs.length);
        const views = copies.reduce((t, c) => t + (c.views || 0), 0);
        const lead = r0.rule === "content_commerce" && views > 0 ? `${views.toLocaleString()} views this month between them` : "";
        why = [lead, namesList(copies.map(c => c.name))].filter(Boolean).join(" · ");
      }
    } else {
      title = plainTitle(r0.title);
      why = reasonFor(r0, moneyVisible);
    }
    if (!moneyVisible) {
      title = scrubMoney(title);
      why = scrubMoney(why);
    }
    const action = inHub ? "hub" : recs.length > 1 ? "draftAll" : "draft";
    rows.push({
      key,
      recs,
      title,
      why,
      impact: imp,
      lift,
      action
    });
  });
  rows.sort((a, b) => b.impact[0] - a.impact[0] || (moneyVisible ? b.lift - a.lift : 0) || b.recs.length - a.recs.length || (Number(b.recs[0].score) || 0) - (Number(a.recs[0].score) || 0));
  return rows;
}

// ---- "What it did": one plain sentence per event
export const EVENT_TONE = {
  analyzed: "grey",
  recommended: "blue",
  drafted: "amber",
  approved: "green",
  launched: "green",
  resumed: "green",
  adjusted: "blue",
  paused: "amber",
  stopped: "red",
  rejected: "red",
  dismissed: "grey",
  reverted: "grey",
  imported: "grey",
  config: "grey"
};
export function eventSentence(e, ctx) {
  const s = String(e.summary || "");
  const byUser = /^user:/i.test(String(e.actor || ""));
  const detail = e.detail && typeof e.detail === "object" ? e.detail : {};
  const recTitle = () => plainTitle(detail.Title || detail.title || "");
  const q = t => `“${plainTitle(t)}”`;
  let m;
  let text;
  switch (e.type) {
    case "analyzed":
      {
        m = s.match(/Analyzed (\d+)-day sales/i);
        const span = m ? `${m[1]} days of sales` : "your sales";
        text = `Looked at ${span}${ctx && ctx.runHasIdeas && e.runId && !ctx.runHasIdeas[e.runId] ? ": nothing new" : ""}`;
        break;
      }
    case "recommended":
      if (m = s.match(/^(\d+) new recommendation/i)) text = `Found ${m[1]} new idea${m[1] === "1" ? "" : "s"}`;else if (m = s.match(/^Proposed:\s*(.+)$/i)) text = `Suggested: ${plainTitle(m[1])}`;else text = "Found new ideas";
      break;
    case "drafted":
      if (m = s.match(/^Drafted flight (.+?): awaiting/i)) text = `Drafted a campaign for ${plainTitle(m[1])}`;else if (m = s.match(/^Drafted journey (.+?): awaiting/i)) text = `Drafted an email journey: ${plainTitle(m[1])}`;else if (m = s.match(/^Drafted market brief for (.+?):/i)) text = `Drafted a report for ${plainText(m[1])}`;else if (m = s.match(/^Drafted content piece (.+?): open/i)) text = `Drafted a piece: ${plainTitle(m[1])}`;else if (m = s.match(/^Reviewed edit set:\s*(.+)$/i)) text = `Got an image edit ready: ${plainTitle(m[1])}`;else text = `Drafted ${lcFirst(recTitle() || plainTitle(s))}`;
      break;
    case "approved":
      text = `${byUser ? "You approved" : "Approved"} ${q(s.replace(/^Approved:\s*/i, ""))}`;
      break;
    case "rejected":
      text = `${byUser ? "You turned down" : "Turned down"} ${q(s.replace(/^Rejected:\s*/i, "").replace(/\s*\([^)]*\)\s*$/, ""))}`;
      break;
    case "dismissed":
      text = `${byUser ? "You dismissed" : "Dismissed"} ${q(s.replace(/^Dismissed:\s*/i, ""))}`;
      break;
    case "reverted":
      text = `${byUser ? "You undid" : "Undid"} ${q(s.replace(/^Reverted:\s*/i, ""))}`;
      break;
    case "config":
      text = /\(off\)/i.test(s) ? `${byUser ? "You" : "Someone"} paused Autopilot or changed its rules` : `${byUser ? "You" : "Someone"} changed its rules`;
      break;
    case "paused":
      text = `Paused ${lcFirst(plainTitle(s).replace(/^Pause\s+/i, ""))}`;
      break;
    case "launched":
      text = `Launched ${lcFirst(plainTitle(s))}`;
      break;
    case "resumed":
      text = `Turned back on ${lcFirst(plainTitle(s))}`;
      break;
    case "stopped":
      text = `Stopped ${lcFirst(plainTitle(s).replace(/^(Pause|Stop)\s+/i, ""))}`;
      break;
    case "adjusted":
      text = plainTitle(s).replace(/^Shift\b/, "Moved").replace(/^Rotate\b/, "Swapped");
      break;
    default:
      text = plainTitle(s) || "Updated";
  }
  text = text.replace(/\buser:\S+/gi, "").replace(/\s{2,}/g, " ").trim();
  return ctx && ctx.moneyVisible ? text : scrubMoney(text);
}